import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Modal, Form, Alert, Nav, Badge, InputGroup } from 'react-bootstrap';
import { getUsers, createUser, updateUser, deleteUser, getPermissionRegistry } from '../../../services/UsersService';
import {
    getApiKeys, createApiKey, deleteApiKey, revokeApiKey, activateApiKey,
    regenerateApiKey, revealApiKey, setApiKeyExpiry,
    getBearerTokens, createBearerToken, deleteBearerToken, revokeBearerToken,
    activateBearerToken, revealBearerToken, extendBearerToken,
} from '../../../services/ApiKeysService';
import { canViewModule, getCurrentUserId, getAuthToken, getSavedUser, hasPermission, isSuperAdmin } from '../../../services/AuthService';
import { getEffectivePermissions, getRoleDefinitions } from '../../../utils/permissions';
import MainPagetitle from '../../layouts/MainPagetitle';
import PermissionMatrix from './PermissionMatrix';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const EXPIRY_OPTIONS = [
    { key: 'noExpiry', value: 0 },
    { key: '30days', value: 30 },
    { key: '90days', value: 90 },
    { key: '180days', value: 180 },
    { key: '365days', value: 365 },
    { key: '730days', value: 730 },
];

function ExpiryBadge({ expires_at, is_expired }) {
    const { t } = useTranslation();
    if (!expires_at) return <Badge bg="secondary">{t('apiKeys.noExpiry')}</Badge>;
    const date = new Date(expires_at);
    const daysLeft = Math.ceil((date - new Date()) / 86400000);
    if (is_expired) return <Badge bg="danger">{t('apiKeys.expired')}</Badge>;
    if (daysLeft <= 30) return <Badge bg="warning" text="dark">{date.toLocaleDateString()} ({daysLeft}d left)</Badge>;
    return <Badge bg="success">{date.toLocaleDateString()}</Badge>;
}

function ExpirySelect({ value, onChange }) {
    const { t } = useTranslation();
    return (
        <Form.Select value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))}>
            {EXPIRY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{t(`apiKeys.expiryOptions.${o.key}`)}</option>
            ))}
        </Form.Select>
    );
}

/** Password-gated action modal. Calls onConfirm(password) on submit. */
function PasswordModal({ show, onHide, title, description, onConfirm, confirmLabel, error }) {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const handleSubmit = (e) => {
        e.preventDefault();
        onConfirm(password);
    };
    const handleHide = () => { setPassword(''); onHide(); };
    return (
        <Modal show={show} onHide={handleHide}>
            <Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {description && <p className="text-muted">{description}</p>}
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form.Group>
                        <Form.Label>{t('users.superAdminPasswordLabel')}</Form.Label>
                        <Form.Control
                            type="password"
                            required
                            autoFocus
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t('common.passwordConfirmPlaceholder')}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleHide}>{t('common.cancel')}</Button>
                    <Button variant="primary" type="submit">{confirmLabel || t('common.confirm')}</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

/** Shows a revealed secret (key or token) once, with copy button. */
function RevealedValueModal({ show, onHide, title, value, label }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(value || '').then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <Modal show={show} onHide={onHide} backdrop="static">
            <Modal.Header><Modal.Title>{title}</Modal.Title></Modal.Header>
            <Modal.Body>
                <Form.Label className="text-muted small">{label || t('common.value')}</Form.Label>
                <InputGroup className="mb-2">
                    <Form.Control
                        readOnly
                        value={value || ''}
                        style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                    />
                    <Button variant={copied ? 'success' : 'outline-secondary'} onClick={copy}>
                        {copied ? t('common.copied') : t('common.copy')}
                    </Button>
                </InputGroup>
                <p className="text-muted small mb-0">
                    {t('common.usage')} <code>Authorization: Bearer &lt;value&gt;</code>
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={onHide}>{t('common.close')}</Button>
            </Modal.Footer>
        </Modal>
    );
}

// ─── API Keys Tab ─────────────────────────────────────────────────────────────

function ApiKeysSection() {
    const { t } = useTranslation();
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Create modal
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', description: '', expires_days: 365 });
    const [createError, setCreateError] = useState('');

    // Reveal modal
    const [revealTarget, setRevealTarget] = useState(null);
    const [revealError, setRevealError] = useState('');
    const [revealedValue, setRevealedValue] = useState(null);

    // Set expiry modal
    const [expiryTarget, setExpiryTarget] = useState(null);
    const [expiryDays, setExpiryDays] = useState(365);
    const [expiryError, setExpiryError] = useState('');

    // New key shown once after create/regenerate
    const [newKey, setNewKey] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await getApiKeys();
            setKeys(res.data || []);
        } catch (err) {
            setError(err.response?.data?.error || t('apiKeys.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreateError('');
        try {
            const res = await createApiKey(createForm);
            setNewKey({ value: res.data.key, name: res.data.name });
            setShowCreate(false);
            setCreateForm({ name: '', description: '', expires_days: 365 });
            load();
        } catch (err) {
            setCreateError(err.response?.data?.error || t('apiKeys.errors.create'));
        }
    };

    const handleRegenerate = async (key) => {
        if (!window.confirm(t('apiKeys.confirmRegenerate', { name: key.name }))) return;
        try {
            const res = await regenerateApiKey(key.id);
            setNewKey({ value: res.data.key, name: res.data.name });
            load();
        } catch (err) {
            setError(err.response?.data?.error || t('apiKeys.errors.regenerate'));
        }
    };

    const handleReveal = async (password) => {
        setRevealError('');
        try {
            const res = await revealApiKey(revealTarget.id, password);
            setRevealTarget(null);
            setRevealedValue({ value: res.data.key, title: t('apiKeys.revealModal.title', { name: revealTarget.name }) });
        } catch (err) {
            setRevealError(err.response?.data?.error || t('apiKeys.errors.reveal'));
        }
    };

    const handleSetExpiry = async (password) => {
        setExpiryError('');
        try {
            await setApiKeyExpiry(expiryTarget.id, password, expiryDays || 0);
            setExpiryTarget(null);
            load();
        } catch (err) {
            setExpiryError(err.response?.data?.error || t('apiKeys.errors.setExpiry'));
        }
    };

    return (
        <>
            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <h5 className="mb-0">{t('apiKeys.heading')}</h5>
                    <p className="text-muted small mb-0">
                        {t('apiKeys.usageLabel')} <code>Authorization: Bearer &lt;key&gt;</code> {t('apiKeys.or')} <code>X-API-Key: &lt;key&gt;</code>
                    </p>
                </div>
                <Button variant="primary" size="sm" onClick={() => { setShowCreate(true); setCreateError(''); }}>
                    {t('apiKeys.generateButton')}
                </Button>
            </div>

            {loading ? <p>{t('common.loading')}</p> : keys.length === 0 ? (
                <Alert variant="secondary">{t('apiKeys.empty')}</Alert>
            ) : (
                <Table responsive hover size="sm" className="api-keys-table">
                    <thead>
                        <tr>
                            <th>{t('apiKeys.columns.name')}</th>
                            <th>{t('apiKeys.columns.prefix')}</th>
                            <th>{t('apiKeys.columns.expiry')}</th>
                            <th>{t('apiKeys.columns.status')}</th>
                            <th>{t('apiKeys.columns.created')}</th>
                            <th>{t('apiKeys.columns.lastUsed')}</th>
                            <th>{t('apiKeys.columns.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {keys.map((k) => (
                            <tr key={k.id}>
                                <td className="align-middle">
                                    <strong>{k.name}</strong>
                                    {k.description && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{k.description}</div>}
                                </td>
                                <td className="align-middle"><code style={{ fontSize: '0.78rem' }}>{k.key_prefix}</code></td>
                                <td className="align-middle"><ExpiryBadge expires_at={k.expires_at} is_expired={k.is_expired} /></td>
                                <td className="align-middle">
                                    <Badge bg={k.is_active && !k.is_expired ? 'success' : 'secondary'}>
                                        {k.is_expired ? t('apiKeys.expired') : k.is_active ? t('apiKeys.status.active') : t('apiKeys.status.revoked')}
                                    </Badge>
                                </td>
                                <td className="align-middle" style={{ whiteSpace: 'nowrap' }}>{k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}</td>
                                <td className="align-middle" style={{ whiteSpace: 'nowrap' }}>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : t('apiKeys.never')}</td>
                                <td className="align-middle">
                                    <div className="d-flex gap-1 flex-wrap">
                                        {k.has_stored_value && (
                                            <Button size="sm" variant="outline-info" onClick={() => { setRevealTarget(k); setRevealError(''); }}>
                                                {t('apiKeys.actions.reveal')}
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline-secondary" onClick={() => { setExpiryTarget(k); setExpiryDays(k.expires_at ? 365 : 365); setExpiryError(''); }}>
                                            {t('apiKeys.actions.setExpiry')}
                                        </Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => handleRegenerate(k)}>{t('apiKeys.actions.regenerate')}</Button>
                                        {k.is_active ? (
                                            <Button size="sm" variant="outline-warning" onClick={async () => { try { await revokeApiKey(k.id); load(); } catch (e) { setError(e.response?.data?.error || t('apiKeys.errors.generic')); } }}>{t('apiKeys.actions.revoke')}</Button>
                                        ) : (
                                            <Button size="sm" variant="outline-success" onClick={async () => { try { await activateApiKey(k.id); load(); } catch (e) { setError(e.response?.data?.error || t('apiKeys.errors.generic')); } }}>{t('apiKeys.actions.activate')}</Button>
                                        )}
                                        <Button size="sm" variant="outline-danger" onClick={async () => { if (!window.confirm(t('apiKeys.confirmDelete', { name: k.name }))) return; try { await deleteApiKey(k.id); load(); } catch (e) { setError(e.response?.data?.error || t('apiKeys.errors.generic')); } }}>{t('apiKeys.actions.delete')}</Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {/* Create modal */}
            <Modal show={showCreate} onHide={() => setShowCreate(false)}>
                <Modal.Header closeButton><Modal.Title>{t('apiKeys.createModal.title')}</Modal.Title></Modal.Header>
                <Form onSubmit={handleCreate}>
                    <Modal.Body>
                        {createError && <Alert variant="danger">{createError}</Alert>}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('apiKeys.createModal.nameLabel')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control required value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder={t('apiKeys.createModal.namePlaceholder')} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('apiKeys.createModal.descriptionLabel')}</Form.Label>
                            <Form.Control as="textarea" rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} placeholder={t('apiKeys.createModal.descriptionPlaceholder')} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('apiKeys.createModal.expiryLabel')}</Form.Label>
                            <ExpirySelect value={createForm.expires_days} onChange={(v) => setCreateForm({ ...createForm, expires_days: v })} />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCreate(false)}>{t('common.cancel')}</Button>
                        <Button variant="primary" type="submit">{t('apiKeys.createModal.generateButton')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Reveal password modal */}
            <PasswordModal
                show={!!revealTarget}
                onHide={() => setRevealTarget(null)}
                title={t('apiKeys.revealModal.title', { name: revealTarget?.name })}
                description={t('apiKeys.revealModal.description')}
                onConfirm={handleReveal}
                confirmLabel={t('apiKeys.revealModal.confirmLabel')}
                error={revealError}
            />

            {/* Set expiry password modal */}
            <Modal show={!!expiryTarget} onHide={() => setExpiryTarget(null)}>
                <Modal.Header closeButton><Modal.Title>{t('apiKeys.setExpiryModal.title', { name: expiryTarget?.name })}</Modal.Title></Modal.Header>
                <Form onSubmit={(e) => { e.preventDefault(); const pw = e.target.password.value; handleSetExpiry(pw); }}>
                    <Modal.Body>
                        {expiryError && <Alert variant="danger">{expiryError}</Alert>}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('apiKeys.setExpiryModal.newExpiryLabel')}</Form.Label>
                            <ExpirySelect value={expiryDays} onChange={setExpiryDays} />
                            <Form.Text className="text-muted">{t('apiKeys.setExpiryModal.helpText')}</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('common.yourPassword')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control type="password" name="password" required placeholder={t('common.passwordConfirmPlaceholder')} />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setExpiryTarget(null)}>{t('common.cancel')}</Button>
                        <Button variant="primary" type="submit">{t('apiKeys.setExpiryModal.saveButton')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* New key shown once */}
            {newKey && (
                <RevealedValueModal
                    show
                    onHide={() => setNewKey(null)}
                    title={t('apiKeys.newKeyModal.title')}
                    value={newKey.value}
                    label={t('apiKeys.newKeyModal.labelPrefix', { name: newKey.name })}
                />
            )}

            {/* Revealed existing key */}
            {revealedValue && (
                <RevealedValueModal
                    show
                    onHide={() => setRevealedValue(null)}
                    title={revealedValue.title}
                    value={revealedValue.value}
                    label={t('apiKeys.revealedModal.label')}
                />
            )}
        </>
    );
}

// ─── Bearer Tokens Section ────────────────────────────────────────────────────

function BearerTokensSection() {
    const { t } = useTranslation();
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Create modal
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', description: '', expires_days: 365 });
    const [createError, setCreateError] = useState('');

    // Reveal modal
    const [revealTarget, setRevealTarget] = useState(null);
    const [revealError, setRevealError] = useState('');
    const [revealedValue, setRevealedValue] = useState(null);

    // Extend modal
    const [extendTarget, setExtendTarget] = useState(null);
    const [extendDays, setExtendDays] = useState(365);
    const [extendError, setExtendError] = useState('');

    // New token shown once
    const [newToken, setNewToken] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await getBearerTokens();
            setTokens(res.data || []);
        } catch (err) {
            setError(err.response?.data?.error || t('bearerTokens.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreateError('');
        try {
            const res = await createBearerToken(createForm);
            setNewToken({ value: res.data.token, name: res.data.name, expires_at: res.data.expires_at });
            setShowCreate(false);
            setCreateForm({ name: '', description: '', expires_days: 365 });
            load();
        } catch (err) {
            setCreateError(err.response?.data?.error || t('bearerTokens.errors.create'));
        }
    };

    const handleReveal = async (password) => {
        setRevealError('');
        try {
            const res = await revealBearerToken(revealTarget.id, password);
            setRevealTarget(null);
            setRevealedValue({ value: res.data.token, title: t('bearerTokens.revealModal.title', { name: revealTarget.name }) });
        } catch (err) {
            setRevealError(err.response?.data?.error || t('bearerTokens.errors.reveal'));
        }
    };

    const handleExtend = async (password) => {
        setExtendError('');
        try {
            const res = await extendBearerToken(extendTarget.id, password, extendDays);
            setExtendTarget(null);
            setNewToken({ value: res.data.token, name: res.data.name, expires_at: res.data.expires_at });
            load();
        } catch (err) {
            setExtendError(err.response?.data?.error || t('bearerTokens.errors.extend'));
        }
    };

    return (
        <>
            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <h5 className="mb-0">{t('bearerTokens.heading')}</h5>
                    <p className="text-muted small mb-0">
                        {t('bearerTokens.usageText')} <code>Authorization: Bearer &lt;token&gt;</code>
                    </p>
                </div>
                <Button variant="primary" size="sm" onClick={() => { setShowCreate(true); setCreateError(''); }}>
                    {t('bearerTokens.generateButton')}
                </Button>
            </div>

            {loading ? <p>{t('common.loading')}</p> : tokens.length === 0 ? (
                <Alert variant="secondary">{t('bearerTokens.empty')}</Alert>
            ) : (
                <Table responsive hover size="sm">
                    <thead>
                        <tr>
                            <th>{t('bearerTokens.columns.name')}</th>
                            <th>{t('bearerTokens.columns.prefix')}</th>
                            <th>{t('bearerTokens.columns.expiry')}</th>
                            <th>{t('bearerTokens.columns.status')}</th>
                            <th>{t('bearerTokens.columns.created')}</th>
                            <th>{t('bearerTokens.columns.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tokens.map((tok) => (
                            <tr key={tok.id}>
                                <td className="align-middle">
                                    <strong>{tok.name}</strong>
                                    {tok.description && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{tok.description}</div>}
                                </td>
                                <td className="align-middle"><code style={{ fontSize: '0.78rem' }}>{tok.token_prefix}</code></td>
                                <td className="align-middle"><ExpiryBadge expires_at={tok.expires_at} is_expired={tok.is_expired} /></td>
                                <td className="align-middle">
                                    <Badge bg={tok.is_active && !tok.is_expired ? 'success' : 'secondary'}>
                                        {tok.is_expired ? t('apiKeys.expired') : tok.is_active ? t('apiKeys.status.active') : t('apiKeys.status.revoked')}
                                    </Badge>
                                </td>
                                <td className="align-middle" style={{ whiteSpace: 'nowrap' }}>{tok.created_at ? new Date(tok.created_at).toLocaleDateString() : '—'}</td>
                                <td className="align-middle">
                                    <div className="d-flex gap-1 flex-wrap">
                                        <Button size="sm" variant="outline-info" onClick={() => { setRevealTarget(tok); setRevealError(''); }}>{t('bearerTokens.actions.reveal')}</Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => { setExtendTarget(tok); setExtendDays(365); setExtendError(''); }}>{t('bearerTokens.actions.extend')}</Button>
                                        {tok.is_active ? (
                                            <Button size="sm" variant="outline-warning" onClick={async () => { try { await revokeBearerToken(tok.id); load(); } catch (e) { setError(e.response?.data?.error || t('bearerTokens.errors.generic')); } }}>{t('bearerTokens.actions.revoke')}</Button>
                                        ) : (
                                            <Button size="sm" variant="outline-success" onClick={async () => { try { await activateBearerToken(tok.id); load(); } catch (e) { setError(e.response?.data?.error || t('bearerTokens.errors.generic')); } }}>{t('bearerTokens.actions.activate')}</Button>
                                        )}
                                        <Button size="sm" variant="outline-danger" onClick={async () => { if (!window.confirm(t('bearerTokens.confirmDelete', { name: tok.name }))) return; try { await deleteBearerToken(tok.id); load(); } catch (e) { setError(e.response?.data?.error || t('bearerTokens.errors.generic')); } }}>{t('bearerTokens.actions.delete')}</Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {/* Create modal */}
            <Modal show={showCreate} onHide={() => setShowCreate(false)}>
                <Modal.Header closeButton><Modal.Title>{t('bearerTokens.createModal.title')}</Modal.Title></Modal.Header>
                <Form onSubmit={handleCreate}>
                    <Modal.Body>
                        {createError && <Alert variant="danger">{createError}</Alert>}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('bearerTokens.createModal.nameLabel')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control required value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder={t('bearerTokens.createModal.namePlaceholder')} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('bearerTokens.createModal.descriptionLabel')}</Form.Label>
                            <Form.Control as="textarea" rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} placeholder={t('bearerTokens.createModal.descriptionPlaceholder')} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('bearerTokens.createModal.expiryLabel')}</Form.Label>
                            <ExpirySelect value={createForm.expires_days} onChange={(v) => setCreateForm({ ...createForm, expires_days: v || 365 })} />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCreate(false)}>{t('common.cancel')}</Button>
                        <Button variant="primary" type="submit">{t('bearerTokens.createModal.generateButton')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Reveal password modal */}
            <PasswordModal
                show={!!revealTarget}
                onHide={() => setRevealTarget(null)}
                title={t('bearerTokens.revealModal.title', { name: revealTarget?.name })}
                description={t('bearerTokens.revealModal.description')}
                onConfirm={handleReveal}
                confirmLabel={t('bearerTokens.revealModal.confirmLabel')}
                error={revealError}
            />

            {/* Extend modal */}
            <Modal show={!!extendTarget} onHide={() => setExtendTarget(null)}>
                <Modal.Header closeButton><Modal.Title>{t('bearerTokens.extendModal.title', { name: extendTarget?.name })}</Modal.Title></Modal.Header>
                <Form onSubmit={(e) => { e.preventDefault(); handleExtend(e.target.password.value); }}>
                    <Modal.Body>
                        {extendError && <Alert variant="danger">{extendError}</Alert>}
                        <Alert variant="info" className="small py-2">
                            {t('bearerTokens.extendModal.infoText')}
                        </Alert>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('bearerTokens.extendModal.newExpiryLabel')}</Form.Label>
                            <ExpirySelect value={extendDays} onChange={setExtendDays} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('common.yourPassword')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control type="password" name="password" required placeholder={t('common.passwordConfirmPlaceholder')} />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setExtendTarget(null)}>{t('common.cancel')}</Button>
                        <Button variant="primary" type="submit">{t('bearerTokens.extendModal.saveButton')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* New token shown once */}
            {newToken && (
                <Modal show backdrop="static">
                    <Modal.Header><Modal.Title>{t('bearerTokens.newTokenModal.title')}</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <Alert variant="warning">
                            <strong>{t('bearerTokens.newTokenModal.warningText')}</strong>
                        </Alert>
                        {newToken.expires_at && (
                            <p className="text-muted small mb-2">{t('bearerTokens.newTokenModal.expiresLabel')} <strong>{new Date(newToken.expires_at).toLocaleString()}</strong></p>
                        )}
                        <CopyField value={newToken.value} label={t('bearerTokens.newTokenModal.labelPrefix', { name: newToken.name })} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={() => setNewToken(null)}>{t('bearerTokens.newTokenModal.closeButton')}</Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Revealed existing token */}
            {revealedValue && (
                <RevealedValueModal
                    show
                    onHide={() => setRevealedValue(null)}
                    title={revealedValue.title}
                    value={revealedValue.value}
                    label={t('bearerTokens.revealedModal.label')}
                />
            )}
        </>
    );
}

function CopyField({ value, label }) {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(value || '').then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <>
            {label && <Form.Label className="text-muted small">{label}</Form.Label>}
            <InputGroup className="mb-2">
                <Form.Control
                    readOnly
                    value={value || ''}
                    style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                />
                <Button variant={copied ? 'success' : 'outline-secondary'} onClick={copy}>
                    {copied ? t('common.copied') : t('common.copy')}
                </Button>
            </InputGroup>
        </>
    );
}

// ─── Full API Keys & Tokens Tab ───────────────────────────────────────────────

function ApiKeysTab() {
    const { t } = useTranslation();
    const currentToken = getAuthToken();
    const savedUser = getSavedUser();

    return (
        <div>
            {/* Session token */}
            <div className="mb-4">
                <h5 className="mb-1">{t('users.sessionToken.heading')}</h5>
                <p className="text-muted small mb-2">
                    {t('users.sessionToken.description')}{' '}
                    <strong>{savedUser?.expireDate ? new Date(savedUser.expireDate).toLocaleString() : t('users.sessionToken.sessionEnd')}</strong>{t('users.sessionToken.descriptionSuffix')}
                </p>
                <CopyField value={currentToken} label={t('users.sessionToken.label')} />
            </div>

            <hr />

            {/* Bearer Tokens */}
            <div className="mb-4">
                <BearerTokensSection />
            </div>

            <hr />

            {/* API Keys */}
            <ApiKeysSection />
        </div>
    );
}

// ─── Users Tab ────────────────────────────────────────────────────────────────

const EMPTY_USER_FORM = {
    username: '',
    email: '',
    password: '',
    role: 'user',
    is_active: true,
    permission_overrides: {},
};

function getRoleChoices({ isSuper, isCreate, superAdminCount, maxSuperAdmins, t }) {
    const options = [{ value: 'user', label: t('users.management.roles.user') }];
    if (isSuper) {
        options.push({ value: 'admin', label: t('users.management.roles.admin') });
        if (isCreate ? superAdminCount < maxSuperAdmins : true) {
            options.push({ value: 'super_admin', label: t('users.management.roles.superAdmin') });
        }
    }
    return options;
}

function cloneOverrides(overrides) {
    return { ...(overrides || {}) };
}

function getRoleHelpText(role, registry) {
    return getRoleDefinitions(registry)[role]?.description || '';
}

function UsersTab() {
    const { t } = useTranslation();
    const currentUser = getSavedUser();
    const currentUserId = getCurrentUserId();
    const actorPermissions = getEffectivePermissions(currentUser);
    const canCreateUsers = actorPermissions.has('users.create');
    const canEditUsers = actorPermissions.has('users.edit');
    const canDeleteUsers = actorPermissions.has('users.delete');
    const canToggleUsers = actorPermissions.has('users.activate') || actorPermissions.has('users.deactivate');
    const canManagePermissions = actorPermissions.has('users.manage_permissions');

    const [users, setUsers] = useState([]);
    const [registryData, setRegistryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [formData, setFormData] = useState(EMPTY_USER_FORM);
    const [editFormData, setEditFormData] = useState(EMPTY_USER_FORM);
    const [submitError, setSubmitError] = useState('');
    const [editSubmitError, setEditSubmitError] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const [usersRes, registryRes] = await Promise.all([
                getUsers(),
                canManagePermissions ? getPermissionRegistry() : Promise.resolve({ data: null }),
            ]);
            setUsers(usersRes.data || []);
            setRegistryData(registryRes.data || null);
        } catch (err) {
            setError(err.response?.data?.error || t('users.management.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const grantablePermissions = new Set(registryData?.grantable_permissions || []);
    const registry = registryData?.registry;
    const superAdminCount = registryData?.super_admin_count || 0;
    const maxSuperAdmins = registryData?.max_super_admins || 3;

    const openCreateModal = () => {
        setSubmitError('');
        setFormData({
            ...EMPTY_USER_FORM,
            role: 'user',
            permission_overrides: {},
        });
        setShowModal(true);
    };

    const openEditModal = (user) => {
        setEditUser(user);
        setEditFormData({
            username: user.username,
            email: user.email,
            password: '',
            role: user.role || 'user',
            is_active: user.is_active !== false,
            permission_overrides: cloneOverrides(user.permission_overrides),
        });
        setEditSubmitError('');
        setShowEditModal(true);
    };

    const canEditTarget = (user) => {
        if (!canEditUsers) return false;
        if (user.role === 'super_admin') return user.id === currentUserId;
        if (isSuperAdmin()) return true;
        return user.role === 'user';
    };

    const canToggleTarget = (user) => {
        if (!canToggleUsers || user.role === 'super_admin') return false;
        if (isSuperAdmin()) return true;
        return user.role === 'user';
    };

    const canDeleteTarget = (user) => {
        if (!canDeleteUsers || user.id === currentUserId || user.role === 'super_admin') return false;
        if (isSuperAdmin()) return true;
        return user.role === 'user';
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitError('');
        try {
            await createUser(formData);
            setShowModal(false);
            setFormData(EMPTY_USER_FORM);
            load();
        } catch (err) {
            setSubmitError(err.response?.data?.error || t('users.management.errors.create'));
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setEditSubmitError('');
        try {
            const payload = {
                username: editFormData.username,
                email: editFormData.email,
            };
            if (editUser.role !== 'super_admin') {
                payload.role = editFormData.role;
                payload.is_active = editFormData.is_active;
                if (editFormData.role !== 'super_admin') {
                    payload.permission_overrides = editFormData.permission_overrides;
                }
                if (editFormData.password) payload.password = editFormData.password;
            }
            await updateUser(editUser.id, payload);
            if (editUser.id === currentUserId) {
                const stored = JSON.parse(localStorage.getItem('userDetails') || '{}');
                stored.username = payload.username;
                stored.email = payload.email;
                localStorage.setItem('userDetails', JSON.stringify(stored));
            }
            setShowEditModal(false);
            setEditUser(null);
            load();
        } catch (err) {
            setEditSubmitError(err.response?.data?.error || t('users.management.errors.update'));
        }
    };

    const handleToggleActive = async (user) => {
        try {
            await updateUser(user.id, { is_active: !user.is_active });
            load();
        } catch (err) {
            setError(err.response?.data?.error || t('users.management.errors.update'));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('users.management.confirmDelete'))) return;
        try {
            await deleteUser(id);
            load();
        } catch (err) {
            setError(err.response?.data?.error || t('users.management.errors.delete'));
        }
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h5 className="mb-0">{t('users.management.heading')}</h5>
                    <p className="text-muted mb-0 small">
                        {t('users.management.description')}
                    </p>
                </div>
                {canCreateUsers && (
                    <Button variant="primary" size="sm" onClick={openCreateModal}>
                        {t('users.management.addButton')}
                    </Button>
                )}
            </div>

            {registryData && (
                <Alert variant="light" className="border">
                    <div className="d-flex flex-wrap gap-3">
                        <div><strong>{t('users.management.superAdminsLabel')}</strong> {superAdminCount}/{maxSuperAdmins}</div>
                        <div><strong>{t('users.management.grantablePermissionsLabel')}</strong> {grantablePermissions.size}</div>
                    </div>
                </Alert>
            )}

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            {loading ? <p>{t('common.loading')}</p> : (
                <Table responsive hover>
                    <thead>
                        <tr>
                            <th>{t('users.management.columns.username')}</th>
                            <th>{t('users.management.columns.email')}</th>
                            <th>{t('users.management.columns.role')}</th>
                            <th>{t('users.management.columns.status')}</th>
                            <th>{t('users.management.columns.permissionSummary')}</th>
                            <th>{t('users.management.columns.created')}</th>
                            <th style={{ minWidth: 180 }}>{t('users.management.columns.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id}>
                                <td className="align-middle">{user.username}</td>
                                <td className="align-middle">{user.email}</td>
                                <td className="align-middle">
                                    <span className={`badge ${user.role === 'super_admin' ? 'bg-danger' : user.role === 'admin' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                        {user.role === 'super_admin' ? t('users.management.roles.superAdmin') : user.role === 'admin' ? t('users.management.roles.admin') : t('users.management.roles.user')}
                                    </span>
                                </td>
                                <td className="align-middle">
                                    <span className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}>{user.is_active ? t('users.management.status.active') : t('users.management.status.inactive')}</span>
                                </td>
                                <td className="align-middle">
                                    <div className="small">{t('users.management.permSummary.effectivePermissions', { count: (user.effective_permissions || []).length })}</div>
                                    <div className="text-muted small">
                                        {Object.keys(user.permission_overrides || {}).length > 0
                                            ? t('users.management.permSummary.customOverrides', { count: Object.keys(user.permission_overrides || {}).length })
                                            : t('users.management.permSummary.roleDefaultsOnly')}
                                    </div>
                                </td>
                                <td className="align-middle">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                                <td className="align-middle">
                                    <div className="d-flex gap-1 flex-wrap">
                                        {canEditTarget(user) && <Button size="sm" variant="outline-primary" onClick={() => openEditModal(user)}>{t('users.management.actions.edit')}</Button>}
                                        {canToggleTarget(user) && (
                                            <Button size="sm" variant={user.is_active ? 'outline-warning' : 'outline-success'} onClick={() => handleToggleActive(user)}>
                                                {user.is_active ? t('users.management.actions.deactivate') : t('users.management.actions.activate')}
                                            </Button>
                                        )}
                                        {canDeleteTarget(user) && (
                                            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(user.id)}>{t('users.management.actions.delete')}</Button>
                                        )}
                                        {user.role === 'super_admin' && user.id !== currentUserId && (
                                            <small className="text-muted">{t('users.management.protectedSuperAdmin')}</small>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal size="xl" show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton><Modal.Title>{editUser ? t('users.editModal.titleWithName', { username: editUser.username }) : t('users.editModal.title')}</Modal.Title></Modal.Header>
                <Form onSubmit={handleEdit}>
                    <Modal.Body>
                        {editSubmitError && <Alert variant="danger">{editSubmitError}</Alert>}
                        {editUser?.role === 'super_admin' && editUser.id !== currentUserId && (
                            <Alert variant="warning">{t('users.editModal.protectedWarning')}</Alert>
                        )}
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('users.editModal.usernameLabel')}</Form.Label>
                                    <Form.Control required value={editFormData.username} onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })} />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('users.editModal.emailLabel')}</Form.Label>
                                    <Form.Control type="email" required value={editFormData.email} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} />
                                </Form.Group>
                            </div>
                        </div>

                        {editUser?.role !== 'super_admin' && (
                            <>
                                <div className="row">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('users.editModal.roleLabel')}</Form.Label>
                                            <Form.Select value={editFormData.role} onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}>
                                                {getRoleChoices({ isSuper: isSuperAdmin(), isCreate: false, superAdminCount, maxSuperAdmins, t }).map((option) => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">{getRoleHelpText(editFormData.role, registry)}</Form.Text>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('users.editModal.passwordLabel')}</Form.Label>
                                            <Form.Control type="password" value={editFormData.password} onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })} placeholder={t('users.editModal.passwordPlaceholder')} />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4 d-flex align-items-end">
                                        <Form.Group className="mb-3">
                                            <Form.Check type="checkbox" label={t('users.editModal.activeLabel')} checked={editFormData.is_active} onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })} />
                                        </Form.Group>
                                    </div>
                                </div>
                                {registry && canManagePermissions && editFormData.role !== 'super_admin' && (
                                    <PermissionMatrix
                                        registry={registry}
                                        grantablePermissions={grantablePermissions}
                                        role={editFormData.role}
                                        value={editFormData.permission_overrides}
                                        onChange={(permission_overrides) => setEditFormData({ ...editFormData, permission_overrides })}
                                    />
                                )}
                                {editFormData.role === 'super_admin' && (
                                    <Alert variant="danger" className="mb-0">
                                        {t('users.editModal.superAdminNote')}
                                    </Alert>
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>{t('common.cancel')}</Button>
                        <Button variant="primary" type="submit">{t('common.save')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal size="xl" show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton><Modal.Title>{t('users.createModal.title')}</Modal.Title></Modal.Header>
                <Form onSubmit={handleCreate}>
                    <Modal.Body>
                        {submitError && <Alert variant="danger">{submitError}</Alert>}
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('users.createModal.usernameLabel')}</Form.Label>
                                    <Form.Control required value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder={t('users.createModal.usernamePlaceholder')} />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('users.createModal.emailLabel')}</Form.Label>
                                    <Form.Control type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={t('users.createModal.emailPlaceholder')} />
                                </Form.Group>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('users.createModal.passwordLabel')}</Form.Label>
                                    <Form.Control type="password" required minLength={6} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder={t('users.createModal.passwordPlaceholder')} />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('users.createModal.roleLabel')}</Form.Label>
                                    <Form.Select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                                        {getRoleChoices({ isSuper: isSuperAdmin(), isCreate: true, superAdminCount, maxSuperAdmins, t }).map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        {isSuperAdmin() ? t('users.createModal.roleHelpSuperAdmin', { max: maxSuperAdmins }) : t('users.createModal.roleHelpNonSuper')}
                                    </Form.Text>
                                    <div className="text-muted small mt-1">{getRoleHelpText(formData.role, registry)}</div>
                                </Form.Group>
                            </div>
                        </div>
                        {registry && canManagePermissions && formData.role !== 'super_admin' && (
                            <PermissionMatrix
                                registry={registry}
                                grantablePermissions={grantablePermissions}
                                role={formData.role}
                                value={formData.permission_overrides}
                                onChange={(permission_overrides) => setFormData({ ...formData, permission_overrides })}
                            />
                        )}
                        {formData.role === 'super_admin' && (
                            <Alert variant="danger" className="mb-0">
                                {t('users.createModal.superAdminNote')}
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</Button>
                        <Button variant="primary" type="submit">{t('users.createModal.createButton')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Users() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('users');
    const canViewUsers = canViewModule('users');
    const canViewApiKeys = hasPermission('api_keys_tokens.view');

    if (!canViewUsers) {
        return (
            <div className="container-fluid">
                <MainPagetitle title={t('users.pageTitle')} />
                <Alert variant="danger">{t('users.accessDenied')}</Alert>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <MainPagetitle title={t('users.pageTitle')} />
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header pb-0">
                            <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
                                <Nav.Item><Nav.Link eventKey="users">{t('users.tabs.users')}</Nav.Link></Nav.Item>
                                {canViewApiKeys && (
                                    <Nav.Item><Nav.Link eventKey="api-keys">{t('users.tabs.apiKeys')}</Nav.Link></Nav.Item>
                                )}
                            </Nav>
                        </div>
                        <div className="card-body pt-3">
                            {activeTab === 'users' && <UsersTab />}
                            {activeTab === 'api-keys' && canViewApiKeys && <ApiKeysTab />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
